import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { Scissors } from "lucide-react";

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@atelier.com");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [notVerified, setNotVerified] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setNotVerified(false); setResendMsg("");
    try {
      const data = await login(email, password);
      if (data.is_super_admin && data.needs_onboarding) nav("/admin");
      else if (data.needs_onboarding) nav("/onboarding");
      else nav("/select-brand");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns 403 with {code: "EMAIL_NOT_VERIFIED", ...} when the
      // password-registered user hasn't verified their email yet.
      if (detail && typeof detail === "object" && detail.code === "EMAIL_NOT_VERIFIED") {
        setNotVerified(true);
        setError("Please verify your email before signing in.");
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    setResendMsg("");
    try {
      const { data } = await api.post("/auth/verify-email/request", { email });
      setResendMsg(data.already_verified
        ? "Your email is already verified — try signing in."
        : "Verification email sent. Please check your inbox.");
    } catch (e2) {
      setResendMsg(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA] text-[#0A0A0A]">
      <div className="hidden md:block relative overflow-hidden border-r border-[#E5E5E5]">
        <img
          src="/img/login-studio.png"
          alt="A brutalist minimal fashion atelier — concrete walls, dark hanging garments, and a pinned mood board"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Subtle vertical gradient — lifts the top slightly and deepens the bottom
            for text legibility without dulling the brutalist studio mood. */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/25 to-black/55"/>
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/branding/logo-mms-white.svg" alt="MMS HUB CENTER" style={{ height: 40 }} />
            <div className="font-serif text-2xl tracking-widest">MMS HUB CENTER</div>
          </div>
          <div>
            <div className="label-caps text-white/80">Fashion Manufacturing</div>
            <h1 className="font-serif font-light text-5xl mt-4 leading-tight text-white">
              Where craft meets<br/>data.
            </h1>
            <p className="text-white/75 text-sm mt-6 max-w-md leading-relaxed">
              A studio-grade ERP for fashion brands, ateliers, and production teams. Tech packs, collections, orders, and workshops — one refined workspace.
            </p>
          </div>
          <div className="text-xs text-white/60 tracking-widest uppercase">© MMS HUB CENTER</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="label-caps text-[#0A0A0A] mb-3">Sign In</div>
          <h2 className="font-serif text-3xl font-light mb-8">Welcome back</h2>

          <label className="label-caps block mb-2">Email</label>
          <input
            data-testid="login-email"
            type="email" required autoFocus
            value={email} onChange={(e)=>setEmail(e.target.value)}
            className="input-dark mb-5"
          />

          <label className="label-caps block mb-2">Password</label>
          <input
            data-testid="login-password"
            type="password" required
            value={password} onChange={(e)=>setPassword(e.target.value)}
            className="input-dark mb-6"
          />

          {error && <div data-testid="login-error" className="text-sm text-red-600 mb-2">{error}</div>}
          {notVerified && (
            <div className="text-xs mb-3 border border-[#E5E5E5] p-3" data-testid="login-not-verified">
              <div className="text-[#171717] mb-1">Email not verified</div>
              <button
                type="button"
                onClick={resendVerification}
                data-testid="login-resend-verification"
                className="text-[#0A0A0A] hover:text-[#171717] uppercase tracking-widest text-[10px]"
              >
                Resend verification email
              </button>
              {resendMsg && <div className="text-[#525252] mt-2" data-testid="login-resend-msg">{resendMsg}</div>}
            </div>
          )}

          <button data-testid="login-submit" disabled={loading} className="btn-gold w-full disabled:opacity-50">
            {loading ? "Signing in…" : "Enter MMS Hub Center"}
          </button>

          <div className="text-right mt-3">
            <Link to="/forgot-password" className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A]" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E5E5E5]"/>
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">or</div>
            <div className="flex-1 h-px bg-[#E5E5E5]"/>
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="login-google"
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors text-sm text-[#0A0A0A]"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.6l6.2 5.2c-.4.4 6.4-4.7 6.4-14.8 0-1.3-.1-2.6-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="text-center text-xs text-[#737373] mt-8">
            No account? <Link to="/register" className="text-[#0A0A0A] hover:text-[#171717]">Create client access</Link>
          </div>
          <div className="text-center text-[10px] text-[#737373] mt-4 uppercase tracking-widest">
            Demo — admin@atelier.com / admin123
          </div>
        </form>
      </div>
    </div>
  );
}
